import React, { useState } from "react";
import { Box, Card, CardContent, Stack, Typography, useTheme } from "@mui/material";

import { getPillButtonSx } from "../../theme/checkinsStyles";

const CheckinCard = ({ item, onOpen }) => {
  const theme = useTheme();
  const [isExpanded, setIsExpanded] = useState(false);
  const isDisabled = Boolean(item.isDisabled);

  const handleOpen = () => {
    if (isDisabled) {
      return;
    }
    onOpen(item.dialogType);
  };

  const handleReadMore = (e) => {
    e.stopPropagation();
    setIsExpanded(!isExpanded);
  };

  return (
    <Card
      role={isDisabled ? undefined : "button"}
      tabIndex={isDisabled ? -1 : 0}
      aria-disabled={isDisabled}
      elevation={10}
      sx={{
        borderRadius: 3,
        cursor: isDisabled ? "not-allowed" : "pointer",
        userSelect: "none",
        width: "100%",
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        transition: "transform 160ms ease",
        opacity: isDisabled ? 0.55 : 1,
        filter: isDisabled ? "grayscale(0.3)" : "none",
        pointerEvents: isDisabled ? "none" : "auto",
        "&:hover": isDisabled
          ? undefined
          : {
              transform: "translateY(-2px)",
            },
        "&:focus-visible": {
          outline: "3px solid rgba(25,118,210,0.35)",
          outlineOffset: 3,
        },
      }}
      onClick={handleOpen}
      onKeyDown={(e) => {
        if (isDisabled) {
          return;
        }
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          handleOpen();
        }
      }}
    >
      <Box
        sx={{
          width: "100%",
          position: "relative",
          backgroundColor: theme.palette.action.hover,
          pt: "56.25%", // 16:9 ratio
        }}
      >
        {item.isCompleted && (
          <Box
            sx={{
              position: "absolute",
              top: 12,
              left: 12,
              zIndex: 2,
              px: 1.2,
              py: 0.4,
              borderRadius: 999,
              fontSize: "0.7rem",
              fontWeight: 600,
              letterSpacing: 0.3,
              color: theme.palette.text.primary,
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              border: `1px solid ${theme.palette.divider}`,
              boxShadow: "0 4px 10px rgba(0,0,0,0.08)",
            }}
          >
            Completed today
          </Box>
        )}
        <Box
          component="img"
          src={item.imageSrc}
          alt={item.imageAlt || item.title}
          onError={(e) => {
            if (item.fallbackSrc && e.currentTarget.src !== item.fallbackSrc) {
              e.currentTarget.src = item.fallbackSrc;
            }
          }}
          sx={{
            position: "absolute",
            inset: 0,
            width: "100%",
            height: "100%",
            objectFit: "cover",
            display: "block",
          }}
        />
      </Box>

      <CardContent
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: "1 1 auto",
          p: { xs: 2, sm: 2.5 },
          gap: 1.25,
        }}
      >
        <Stack spacing={1} sx={{ minWidth: 0 }}>
          <Typography
            variant="h6"
            fontWeight={800}
            sx={{ color: "text.primary", lineHeight: 1.15 }}
          >
            {item.title}
          </Typography>

          <Typography
            variant="caption"
            sx={{
              color: "text.secondary",
              letterSpacing: 0.6,
              lineHeight: 1.2,
            }}
          >
            Daily check-ins
          </Typography>

          <Typography
            variant="body2"
            sx={{
              color: "text.secondary",
              lineHeight: 1.75,
              display: "-webkit-box",
              WebkitLineClamp: isExpanded ? "unset" : 3,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
              cursor: "pointer",
            }}
            onClick={handleReadMore}
          >
            {item.description}
            {!isExpanded && item.description.split('\n').length > 3 && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                ...More
              </Typography>
            )}
            {isExpanded && (
              <Typography
                component="span"
                variant="body2"
                sx={{
                  color: "text.secondary",
                  fontWeight: 500,
                  ml: 0.5,
                }}
              >
                Less
              </Typography>
            )}
          </Typography>
        </Stack>

        <Box sx={{ mt: "auto", pt: 1, display: "flex", justifyContent: "flex-start" }}>
          <Box
            component="button"
            type="button"
            aria-disabled={isDisabled}
            sx={{
              ...getPillButtonSx(theme),
              cursor: isDisabled ? "not-allowed" : "pointer",
              backgroundImage: isDisabled ? "none" : undefined,
              backgroundColor: isDisabled
                ? theme.palette.action.disabledBackground
                : undefined,
              color: isDisabled ? theme.palette.text.disabled : undefined,
              border: isDisabled
                ? `1px solid ${theme.palette.action.disabled}`
                : undefined,
              boxShadow: isDisabled ? "none" : undefined,
            }}
            onClick={(e) => {
              e.stopPropagation();
              handleOpen();
            }}
          >
            {item.isCompleted ? `Retake ${item.categoryTitle}` : item.categoryTitle}
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default CheckinCard;
